/**
 * SECCIÓN H — Sub-pestaña "Eventos" en Soporte PEMEX.
 * CRUD + búsqueda + exportes XLSX/PDF + modal de evidencias con drag&drop.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Modal } from "./ui";
import {
  Download, Edit2, Loader2, Plus, Search, Trash2, Upload, X, Save, Paperclip,
} from "lucide-react";

const TIPOS_EVENTO = [
  "Derrame HC", "Derrame Químico", "Acumulación de Residuos",
  "Emisión Atmosférica", "Afectación Flora", "Afectación Fauna",
  "Vertimiento", "Otro",
];
const TIPO_COLORS = {
  "Derrame HC": "#D50000",
  "Derrame Químico": "#E65100",
  "Acumulación de Residuos": "#F57F17",
  "Emisión Atmosférica": "#0277BD",
  "Afectación Flora": "#2E7D32",
  "Afectación Fauna": "#1B5E20",
  "Vertimiento": "#1565C0",
  "Otro": "#546E7A",
};
const STATUSES = [
  "Activo", "En Atención", "Contenido",
  "Remediado", "Cerrado", "Reportado PROFEPA",
];
const STATUS_COLORS = {
  "Activo": "#D50000",
  "En Atención": "#E65100",
  "Contenido": "#F57F17",
  "Remediado": "#0277BD",
  "Cerrado": "#2E7D32",
  "Reportado PROFEPA": "#6A1B9A",
};
const MEDIOS = [
  "Suelo", "Agua Superficial", "Agua Subterránea",
  "Aire", "Flora", "Fauna", "Múltiple",
];
const UNIDADES = ["litros", "m³", "kg", "m²", "ton", "otro"];
const ALLOWED_FILE_EXTS = "PDF, JPG, PNG, WEBP, DOCX, XLSX · Máx 20 MB";

function ColoredBadge({ label, color }) {
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        border: `1px solid ${color}55`,
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {label}
    </span>
  );
}

function fmtDate(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  } catch {
    return s;
  }
}

function isoLocalForInput(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return "";
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  } catch {
    return "";
  }
}

export default function PemexEventos() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [editing, setEditing] = useState(null);   // evt object or "new"
  const [evidenciasFor, setEvidenciasFor] = useState(null); // evt object
  const [importOpen, setImportOpen] = useState(false);
  const [toast, setToast] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit };
      if (search) params.search = search;
      const r = await api.get("/pemex/eventos", { params });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [skip, limit, search]);

  useEffect(() => {
    const id = setTimeout(reload, 250);
    return () => clearTimeout(id);
  }, [reload]);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  }

  async function handleDelete(evt) {
    if (!window.confirm(`¿Eliminar el evento ${evt.id}? Se eliminarán también sus evidencias.`)) return;
    try {
      await api.delete(`/pemex/eventos/${evt.id}`);
      showToast(`Evento ${evt.id} eliminado`);
      await reload();
    } catch (e) {
      alert("Error eliminando: " + (e?.response?.data?.detail || e.message));
    }
  }

  async function downloadBlob(path, filename) {
    try {
      const res = await api.post(path, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      showToast(`${filename} descargado`);
    } catch (e) {
      alert("Error descargando: " + (e?.response?.data?.detail || e.message));
    }
  }

  const hasMore = skip + items.length < total;

  return (
    <div data-testid="pemex-eventos-panel" className="space-y-3">
      {/* Barra superior */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2" style={{ flex: 1, minWidth: 240 }}>
          <Search size={14} className="text-dim" />
          <input
            className="input"
            placeholder="Buscar eventos..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
            data-testid="pevt-search"
            style={{ maxWidth: 380 }}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn" onClick={() => downloadBlob("/pemex/eventos/export/xlsx", `eventos_${new Date().toISOString().slice(0, 10)}.xlsx`)} data-testid="pevt-export-xlsx">
            <Download size={14} /> Exportar XLSX
          </button>
          <button className="btn" onClick={() => downloadBlob("/pemex/eventos/export/pdf", `eventos_${new Date().toISOString().slice(0, 10)}.pdf`)} data-testid="pevt-export-pdf">
            <Download size={14} /> Exportar PDF
          </button>
          <button className="btn" onClick={() => setImportOpen(true)} data-testid="pevt-import">
            <Upload size={14} /> Importar
          </button>
          <button className="btn btn-primary" onClick={() => setEditing("new")} data-testid="pevt-new">
            <Plus size={14} /> Nuevo
          </button>
        </div>
      </div>

      {toast && (
        <div className="card" style={{ background: "rgba(0,200,83,0.10)", borderColor: "#00C853" }} data-testid="pevt-toast">{toast}</div>
      )}

      {/* Tabla */}
      <div className="card" style={{ padding: 0, overflowX: "auto" }} data-testid="pevt-table-wrap">
        <table className="w-full" style={{ borderCollapse: "collapse", minWidth: 1100 }}>
          <thead>
            <tr style={{ background: "var(--bg-2)", textAlign: "left" }}>
              <th style={th}>#&nbsp;EVENTO</th>
              <th style={th}>TIPO</th>
              <th style={th}>DESCRIPCIÓN</th>
              <th style={th}>FECHA Y HORA</th>
              <th style={th}>ÁREA</th>
              <th style={th}>MEDIO AFECT.</th>
              <th style={th}>STATUS</th>
              <th style={th}>EVIDENCIAS</th>
              <th style={th}>ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={td} className="text-dim text-center">Cargando eventos…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={9} style={td} className="text-dim text-center">Sin eventos registrados.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} style={{ borderTop: "1px solid var(--border-2)" }} data-testid={`pevt-row-${it.id}`}>
                <td style={{ ...td, fontFamily: "ui-monospace,monospace", whiteSpace: "nowrap" }}>{it.id}</td>
                <td style={td}><ColoredBadge label={it.tipo_evento} color={TIPO_COLORS[it.tipo_evento] || "#546E7A"} /></td>
                <td style={td} title={it.descripcion}>{(it.descripcion || "").length > 60 ? (it.descripcion || "").slice(0, 60) + "…" : (it.descripcion || "")}</td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDate(it.fecha_hora_evento)}</td>
                <td style={td}>{it.area_instalacion || ""}</td>
                <td style={td}>
                  <div className="flex gap-1 flex-wrap">
                    {(it.medio_afectado || []).map((m) => (
                      <span key={m} style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", padding: "1px 6px", borderRadius: 3, fontSize: 10 }}>{m}</span>
                    ))}
                  </div>
                </td>
                <td style={td}><ColoredBadge label={it.status} color={STATUS_COLORS[it.status] || "#546E7A"} /></td>
                <td style={td}>
                  {(it.evidencias || []).length === 0 ? (
                    <button className="btn btn-sm" onClick={() => setEvidenciasFor(it)} data-testid={`pevt-evid-add-${it.id}`}>
                      <Plus size={12} /> Agregar
                    </button>
                  ) : (
                    <button className="btn btn-sm" onClick={() => setEvidenciasFor(it)} data-testid={`pevt-evid-manage-${it.id}`}>
                      <Paperclip size={12} /> {(it.evidencias || []).length} archivo{(it.evidencias || []).length !== 1 ? "s" : ""}
                    </button>
                  )}
                </td>
                <td style={td}>
                  <div className="flex gap-1">
                    <button className="btn btn-sm" onClick={() => setEditing(it)} data-testid={`pevt-edit-${it.id}`} title="Editar"><Edit2 size={12} /></button>
                    <button className="btn btn-sm" style={{ color: "#D50000" }} onClick={() => handleDelete(it)} data-testid={`pevt-delete-${it.id}`} title="Eliminar"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs text-dim">{total} eventos · mostrando {items.length}</div>
        <div className="flex gap-2">
          <button className="btn btn-sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="pevt-prev">Anterior</button>
          <button className="btn btn-sm" disabled={!hasMore} onClick={() => setSkip(skip + limit)} data-testid="pevt-next">Siguiente</button>
        </div>
      </div>

      {/* Modal crear/editar */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "Nuevo Evento Ambiental" : `Editar ${editing?.id || ""}`}
        size="lg"
        testId="pevt-modal-edit"
      >
        {editing && (
          <EventoForm
            initial={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={async (saved, isNew) => {
              setEditing(null);
              showToast(isNew ? `Evento ${saved.id} creado` : `Evento ${saved.id} actualizado`);
              await reload();
            }}
          />
        )}
      </Modal>

      {/* Modal evidencias */}
      <Modal
        open={!!evidenciasFor}
        onClose={() => setEvidenciasFor(null)}
        title={`Evidencias — ${evidenciasFor?.id || ""}`}
        size="lg"
        testId="pevt-modal-evid"
      >
        {evidenciasFor && (
          <EvidenciasManager
            evento={evidenciasFor}
            onChange={async (updated) => { setEvidenciasFor(updated); await reload(); }}
          />
        )}
      </Modal>

      {/* Modal importar (placeholder) */}
      <Modal open={importOpen} onClose={() => setImportOpen(false)} title="Importar Eventos" size="md" testId="pevt-modal-import">
        <div className="text-dim text-sm">Próximamente.</div>
        <div className="flex justify-end mt-3"><button className="btn" onClick={() => setImportOpen(false)}>Cerrar</button></div>
      </Modal>
    </div>
  );
}

const th = { padding: "8px 10px", fontSize: 11, fontWeight: 600, color: "var(--text-2)", borderBottom: "1px solid var(--border-2)", whiteSpace: "nowrap" };
const td = { padding: "8px 10px", fontSize: 12, verticalAlign: "top" };

// ─────────────────────────────────────────────────────────────────
function EventoForm({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState(() => ({
    tipo_evento: initial?.tipo_evento || "Otro",
    descripcion: initial?.descripcion || "",
    fecha_hora_evento: initial?.fecha_hora_evento ? isoLocalForInput(initial.fecha_hora_evento) : "",
    area_instalacion: initial?.area_instalacion || "",
    medio_afectado: initial?.medio_afectado || [],
    volumen_cantidad: initial?.volumen_cantidad ?? "",
    unidad_medida: initial?.unidad_medida || "litros",
    sustancia_residuo: initial?.sustancia_residuo || "",
    acciones_inmediatas: initial?.acciones_inmediatas || "",
    status: initial?.status || "Activo",
    responsable_atencion: initial?.responsable_atencion || "",
    reportado_profepa: !!initial?.reportado_profepa,
    fecha_reporte_profepa: initial?.fecha_reporte_profepa ? String(initial.fecha_reporte_profepa).slice(0, 10) : "",
    observaciones: initial?.observaciones || "",
  }));
  const [saving, setSaving] = useState(false);
  const setF = (patch) => setForm((s) => ({ ...s, ...patch }));
  const toggleMedio = (m) => setF({ medio_afectado: form.medio_afectado.includes(m) ? form.medio_afectado.filter((x) => x !== m) : [...form.medio_afectado, m] });

  function validate() {
    if (!form.tipo_evento) return "Tipo de Evento requerido";
    if (!form.descripcion?.trim()) return "Descripción requerida";
    if (!form.fecha_hora_evento) return "Fecha y Hora requeridas";
    if (!form.area_instalacion?.trim()) return "Área / Instalación requerida";
    if (!form.medio_afectado || form.medio_afectado.length === 0) return "Medio Afectado requerido";
    if (!form.status) return "Status requerido";
    if (form.reportado_profepa && !form.fecha_reporte_profepa) return "Fecha de Reporte a PROFEPA requerida";
    return null;
  }

  async function save() {
    const err = validate();
    if (err) { alert(err); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        fecha_hora_evento: form.fecha_hora_evento ? new Date(form.fecha_hora_evento).toISOString() : null,
        fecha_reporte_profepa: form.reportado_profepa && form.fecha_reporte_profepa ? new Date(form.fecha_reporte_profepa).toISOString() : null,
        volumen_cantidad: form.volumen_cantidad === "" ? 0 : Number(form.volumen_cantidad),
      };
      let saved;
      if (isNew) {
        const r = await api.post("/pemex/eventos", payload);
        saved = r.data;
      } else {
        const r = await api.put(`/pemex/eventos/${initial.id}`, payload);
        saved = r.data;
      }
      onSaved?.(saved, isNew);
    } catch (e) {
      alert("Error guardando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3" data-testid="pevt-form">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Tipo de Evento *">
          <select className="input" value={form.tipo_evento} onChange={(e) => setF({ tipo_evento: e.target.value })} data-testid="pevt-f-tipo">
            {TIPOS_EVENTO.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Fecha y Hora del Evento *">
          <input type="datetime-local" className="input" value={form.fecha_hora_evento} onChange={(e) => setF({ fecha_hora_evento: e.target.value })} data-testid="pevt-f-fecha" />
        </Field>
      </div>
      <Field label="Descripción *">
        <textarea className="input" rows={3} value={form.descripcion} onChange={(e) => setF({ descripcion: e.target.value })} data-testid="pevt-f-desc" />
      </Field>
      <Field label="Área / Instalación *">
        <input className="input" value={form.area_instalacion} onChange={(e) => setF({ area_instalacion: e.target.value })} data-testid="pevt-f-area" />
      </Field>
      <Field label="Medio Afectado *">
        <div className="grid grid-cols-2 gap-1">
          {MEDIOS.map((m) => (
            <label key={m} className="flex items-center gap-2" style={{ fontSize: 12 }}>
              <input type="checkbox" checked={form.medio_afectado.includes(m)} onChange={() => toggleMedio(m)} data-testid={`pevt-f-medio-${m}`} />
              {m}
            </label>
          ))}
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Volumen / Cantidad">
          <div className="flex gap-1">
            <input type="number" className="input" value={form.volumen_cantidad} onChange={(e) => setF({ volumen_cantidad: e.target.value })} data-testid="pevt-f-vol" />
            <select className="input" value={form.unidad_medida} onChange={(e) => setF({ unidad_medida: e.target.value })} style={{ maxWidth: 110 }} data-testid="pevt-f-unidad">
              {UNIDADES.map((u) => <option key={u}>{u}</option>)}
            </select>
          </div>
        </Field>
        <Field label="Sustancia / Residuo">
          <input className="input" value={form.sustancia_residuo} onChange={(e) => setF({ sustancia_residuo: e.target.value })} data-testid="pevt-f-sustancia" />
        </Field>
      </div>
      <Field label="Acciones Inmediatas Tomadas">
        <textarea className="input" rows={3} value={form.acciones_inmediatas} onChange={(e) => setF({ acciones_inmediatas: e.target.value })} data-testid="pevt-f-acciones" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Status *">
          <select className="input" value={form.status} onChange={(e) => setF({ status: e.target.value })} data-testid="pevt-f-status">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Responsable de Atención">
          <input className="input" value={form.responsable_atencion} onChange={(e) => setF({ responsable_atencion: e.target.value })} data-testid="pevt-f-resp" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Reportado a PROFEPA">
          <label className="flex items-center gap-2" style={{ fontSize: 12 }}>
            <input type="checkbox" checked={form.reportado_profepa} onChange={(e) => setF({ reportado_profepa: e.target.checked })} data-testid="pevt-f-profepa" />
            {form.reportado_profepa ? "Sí" : "No"}
          </label>
        </Field>
        {form.reportado_profepa && (
          <Field label="Fecha de Reporte a PROFEPA *">
            <input type="date" className="input" value={form.fecha_reporte_profepa} onChange={(e) => setF({ fecha_reporte_profepa: e.target.value })} data-testid="pevt-f-fecha-profepa" />
          </Field>
        )}
      </div>
      <Field label="Observaciones">
        <textarea className="input" rows={2} value={form.observaciones} onChange={(e) => setF({ observaciones: e.target.value })} data-testid="pevt-f-obs" />
      </Field>
      <div className="flex justify-end gap-2 mt-3">
        <button className="btn" onClick={onClose} disabled={saving}>Cancelar</button>
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="pevt-f-save">
          {saving ? <Loader2 size={12} className="spin" /> : <Save size={12} />} Guardar
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-dim mb-1">{label}</div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function EvidenciasManager({ evento, onChange }) {
  const [evt, setEvt] = useState(evento);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  async function upload(files) {
    if (!files || files.length === 0) return;
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const res = await api.post(`/pemex/eventos/${evt.id}/evidencias/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setEvt(res.data);
      onChange?.(res.data);
    } catch (e) {
      alert("Error subiendo: " + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false); setProgress(0);
    }
  }

  async function removeFile(file_id) {
    if (!window.confirm("¿Eliminar este archivo?")) return;
    try {
      await api.delete(`/pemex/eventos/${evt.id}/evidencias/${file_id}`);
      const updated = { ...evt, evidencias: (evt.evidencias || []).filter((f) => f.file_id !== file_id) };
      setEvt(updated);
      onChange?.(updated);
    } catch (e) {
      alert("Error eliminando: " + (e?.response?.data?.detail || e.message));
    }
  }

  async function download(f) {
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${api.defaults.baseURL}/pemex/eventos/${evt.id}/evidencias/${f.file_id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Error descargando: " + e.message);
    }
  }

  return (
    <div className="space-y-3" data-testid="pevt-evid-modal">
      <div
        className={`dropzone ${dragActive ? "active" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => { e.preventDefault(); setDragActive(false); upload(e.dataTransfer.files); }}
        style={{ cursor: "pointer", border: `2px dashed ${dragActive ? "#1565C0" : "var(--border-2)"}`, borderRadius: 6, padding: 20, textAlign: "center", background: dragActive ? "rgba(21,101,192,0.08)" : "transparent" }}
        data-testid="pevt-evid-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,.xlsx"
          style={{ display: "none" }}
          onChange={(e) => upload(e.target.files)}
          data-testid="pevt-evid-file-input"
        />
        <div className="font-semibold">Arrastra archivos o haz clic para seleccionar</div>
        <div className="text-xs text-dim mt-1">{ALLOWED_FILE_EXTS}</div>
        {uploading && (
          <div className="mt-3" data-testid="pevt-evid-progress">
            <div style={{ background: "var(--bg-2)", height: 6, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ width: `${progress}%`, height: "100%", background: "#1565C0", transition: "width 200ms" }} />
            </div>
            <div className="text-xs text-dim mt-1">Subiendo… {progress}%</div>
          </div>
        )}
      </div>

      <div>
        <div className="text-xs text-dim mb-1">Archivos ({(evt.evidencias || []).length})</div>
        {(evt.evidencias || []).length === 0 ? (
          <div className="text-dim text-sm" data-testid="pevt-evid-empty">Sin evidencias adjuntas</div>
        ) : (
          <div className="space-y-1">
            {(evt.evidencias || []).map((f) => (
              <div key={f.file_id} className="flex items-center justify-between gap-2" style={{ padding: "6px 8px", border: "1px solid var(--border-2)", borderRadius: 4 }} data-testid={`pevt-evid-row-${f.file_id}`}>
                <div className="flex items-center gap-2" style={{ flex: 1, minWidth: 0 }}>
                  <Paperclip size={12} className="text-dim" />
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{f.original_name}</div>
                    <div className="text-xs text-dim">{f.size_kb} KB · {fmtDate(f.uploaded_at)}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button className="btn btn-sm" onClick={() => download(f)} title="Descargar" data-testid={`pevt-evid-dl-${f.file_id}`}><Download size={12} /></button>
                  <button className="btn btn-sm" style={{ color: "#D50000" }} onClick={() => removeFile(f.file_id)} title="Eliminar" data-testid={`pevt-evid-del-${f.file_id}`}><X size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
